import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import _ from "lodash";
import { Box } from "@mui/material";
import BaseTable from "@reusables/BaseTable";
import BasePagination from "@reusables/BasePagination";
import BaseSearchInput from "@reusables/BaseSearchInput";
import { createPagination } from "@helpers/utils";
import { StockCount } from "@/types/general";

const ENTRIES_PER_PAGE = 10;

/**
 * Products of a single stock count. Turns into a searchable, paginated table once the count holds
 * more entries than fit on one page, so long counts don't force the user to scroll the whole page.
 */
export default function StockCountEntriesTable({
    entries,
}: {
    entries: StockCount.Components.Entry[];
}) {
    const { t } = useTranslation("", { keyPrefix: "inventory.stockCount.main.subtable" });

    const pagination = createPagination(ENTRIES_PER_PAGE);

    const [search, setSearch] = useState("");
    // Filtering is fully client-side, so a short debounce is enough to keep typing responsive.
    const debouncedSearch = useRef(_.debounce(setSearch, 300)).current;

    // Gated on the unfiltered length so the search box doesn't disappear from under the user while typing.
    const isSearchable = entries.length > pagination.perPage.elementsPerPage;

    const filteredEntries = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) return entries;

        return entries.filter((entry) =>
            [entry.product.name, entry.product.code, entry.store.name, entry.section?.name].some(
                (field) => field?.toLowerCase().includes(query)
            )
        );
    }, [entries, search]);

    useEffect(() => {
        pagination.calculate(filteredEntries);
    }, [filteredEntries]);

    return (
        <div className="space-y-[12px]">
            {isSearchable && (
                <Box
                    className="w-[300px]"
                    sx={{
                        "& .MuiInput-input": {
                            borderColor: "#7556FA",
                            opacity: search ? 1 : 0.5,
                            "&:focus": {
                                opacity: 1,
                            },
                        },
                    }}
                >
                    <BaseSearchInput
                        onSearch={debouncedSearch}
                        placeholder={t("search.placeholder")}
                        permanent
                    />
                </Box>
            )}

            {/* White card lifts the table off the collapse's lavender fill and marks where it ends */}
            <div className="overflow-hidden rounded-lg border border-solid border-[#B3B5EB] bg-white">
                <BaseTable
                    pagination={pagination}
                    data={filteredEntries}
                    columns={[
                        {
                            header: (
                                <div className="text-accent font-semibold">{t("columns.0")}</div>
                            ),
                            // Linked out to the product page: a name alone isn't enough to pin
                            // down which product this is once the catalogue gets large.
                            getter: (subRow) => (
                                <Link to={`/dashboard/products/${subRow.product.id}/details`}>
                                    <u>{subRow.product.name}</u>
                                </Link>
                            ),
                        },
                        {
                            header: (
                                <div className="text-accent font-semibold">{t("columns.1")}</div>
                            ),
                            getter: (subRow) =>
                                subRow.store.name +
                                (subRow.section ? ` - ${subRow.section.name}` : ""),
                        },
                        {
                            header: (
                                <div className="text-accent font-semibold">{t("columns.2")}</div>
                            ),
                            getter: (subRow) => subRow.system_quantity,
                        },
                        {
                            header: (
                                <div className="text-accent font-semibold">{t("columns.3")}</div>
                            ),
                            getter: (subRow) => subRow.counted_quantity,
                        },
                    ]}
                    size="small"
                    nothingFound={{
                        height: 120,
                    }}
                    hideTableWhenNothingFound
                />
            </div>

            {pagination.count.pages > 1 && (
                <div className="flex justify-end">
                    <BasePagination config={pagination} />
                </div>
            )}
        </div>
    );
}
